<template>
  <el-row>
    <el-col :span="24">
      <h3 class="form-title">
        {{ id ? "GFS ma'lumotni tahrirlash" : "GFS ma'lumot qo'shish" }}
      </h3>
    </el-col>
    <el-col :span="24">
      <el-form
        v-loading="loading"
        label-position="top"
        ref="formRef"
        :model="form"
        class="gfs-form"
      >
        <el-form-item
          label="Sektor turi"
          prop="sector_type"
          :rules="[{ required: true, message: 'Sektor turini tanlang' }]"
        >
          <el-select
            v-model="form.sector_type"
            placeholder="Sektor turini tanlang"
            style="width: 100%"
            @change="onSectorChange"
          >
            <el-option
              value="GENERAL_GOVERNMENT"
              label="Davlat boshqaruv sektori"
            />
            <el-option
              value="CENTRAL_GOVERNMENT"
              label="Markaziy hukumat sektori"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Davr turi"
          prop="period_type"
          :rules="[{ required: true, message: 'Davr turini tanlang' }]"
        >
          <el-select
            v-model="form.period_type"
            placeholder="Avval sektor turini tanlang"
            :disabled="!form.sector_type"
            style="width: 100%"
            @change="onPeriodChange"
          >
            <el-option
              v-for="opt in periodOptions"
              :key="opt.value"
              :value="opt.value"
              :label="opt.label"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Yil"
          prop="year"
          :rules="[{ required: true, message: 'Yilni kiriting' }]"
        >
          <el-input-number
            v-model="form.year"
            :min="1900"
            :max="2100"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item
          v-if="showQuarter"
          label="Chorak"
          prop="quarter"
          :rules="[{ required: true, message: 'Chorakni tanlang' }]"
        >
          <el-select
            v-model="form.quarter"
            placeholder="Chorakni tanlang"
            style="width: 100%"
          >
            <el-option :value="1" label="1-chorak" />
            <el-option :value="2" label="2-chorak" />
            <el-option :value="3" label="3-chorak" />
            <el-option :value="4" label="4-chorak" />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="showMonth"
          label="Oy"
          prop="month"
          :rules="[{ required: true, message: 'Oyni tanlang' }]"
        >
          <el-select
            v-model="form.month"
            placeholder="Oyni tanlang"
            style="width: 100%"
          >
            <el-option v-for="m in 12" :key="m" :value="m" :label="`${m}-oy`" />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Excel fayl"
          prop="excel_file_id"
          :rules="[{ validator: validateAtLeastOne, trigger: 'change' }]"
        >
          <el-input v-model="form.excel_file_id" style="display: none" />
          <input type="file" ref="excelFileInputRef" hidden accept=".xls,.xlsx" @change="(e) => uploadFile(e, 'excel')" />
          <div class="file-upload-row">
            <el-button :loading="excelFileLoading" @click="excelFileInputRef.click()">
              {{ form.excel_file_id ? "Excel faylni almashtirish" : "Excel fayl yuklash" }}
            </el-button>
            <span v-if="excelFileName" class="file-name">{{ excelFileName }}</span>
          </div>
        </el-form-item>

        <el-form-item
          label="SDMX fayl"
          prop="sdmx_file_id"
          :rules="[{ validator: validateAtLeastOne, trigger: 'change' }]"
        >
          <el-input v-model="form.sdmx_file_id" style="display: none" />
          <input type="file" ref="sdmxFileInputRef" hidden accept=".xml,.sdmx" @change="(e) => uploadFile(e, 'sdmx')" />
          <div class="file-upload-row">
            <el-button :loading="sdmxFileLoading" @click="sdmxFileInputRef.click()">
              {{ form.sdmx_file_id ? "SDMX faylni almashtirish" : "SDMX fayl yuklash" }}
            </el-button>
            <span v-if="sdmxFileName" class="file-name">{{ sdmxFileName }}</span>
          </div>
          <div class="file-hint">Kamida bitta fayl (Excel yoki SDMX) yuklanishi shart</div>
        </el-form-item>

        <div class="form-actions">
          <router-link
            to="/app/gfs-data"
            class="el-button el-button--danger"
          >
            Bekor qilish
          </router-link>
          <el-button type="primary" :loading="submitLoading" @click="submitForm">
            Saqlash
          </el-button>
        </div>
      </el-form>
    </el-col>
  </el-row>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { ElMessage, ElNotification } from "element-plus";
import http from "@/http";
import store from "@/store";
import router from "@/router";
import { useRoute } from "vue-router";

const route = useRoute();
const { id } = route.params;

const formRef = ref();
const excelFileInputRef = ref();
const sdmxFileInputRef = ref();
const loading = ref(false);
const excelFileLoading = ref(false);
const sdmxFileLoading = ref(false);
const submitLoading = ref(false);
const excelFileName = ref(null);
const sdmxFileName = ref(null);

const form = reactive({
  excel_file_id: "",
  sdmx_file_id: "",
  sector_type: "",
  period_type: "",
  year: new Date().getFullYear(),
  quarter: null,
  month: null,
});

const periodOptions = computed(() => {
  if (form.sector_type === "GENERAL_GOVERNMENT") {
    return [
      { value: "YEARLY", label: "Yillik" },
      { value: "QUARTERLY", label: "Choraklik" },
    ];
  }
  if (form.sector_type === "CENTRAL_GOVERNMENT") {
    return [{ value: "MONTHLY", label: "Oylik" }];
  }
  return [];
});

const showQuarter = computed(
  () =>
    form.sector_type === "GENERAL_GOVERNMENT" &&
    form.period_type === "QUARTERLY"
);
const showMonth = computed(
  () =>
    form.sector_type === "CENTRAL_GOVERNMENT" && form.period_type === "MONTHLY"
);

const onSectorChange = () => {
  form.period_type = "";
  form.quarter = null;
  form.month = null;
};

const onPeriodChange = () => {
  form.quarter = null;
  form.month = null;
};

const validateAtLeastOne = (_, __, callback) => {
  if (!form.excel_file_id && !form.sdmx_file_id) {
    callback(new Error("Kamida bitta fayl (Excel yoki SDMX) yuklanishi shart"));
  } else {
    callback();
  }
};

const EXCEL_EXTS = [".xls", ".xlsx"];
const SDMX_EXTS = [".xml", ".sdmx"];

const uploadFile = (event, type) => {
  const file = event.target.files[0];
  if (!file) return;
  const ext = "." + file.name.split(".").pop().toLowerCase();
  if (type === "excel" && !EXCEL_EXTS.includes(ext)) {
    ElMessage.error("Faqat Excel fayl (.xls, .xlsx) yuklanishi mumkin!");
    event.target.value = "";
    return;
  }
  if (type === "sdmx" && !SDMX_EXTS.includes(ext)) {
    ElMessage.error("Faqat SDMX fayl (.xml, .sdmx) yuklanishi mumkin!");
    event.target.value = "";
    return;
  }
  if (file.size / 1024 / 1024 > 20) {
    ElMessage.error("Fayl xajmi max. 20MB bo'lishi kerak!");
    event.target.value = "";
    return;
  }
  const formData = new FormData();
  formData.append("file", file);
  if (type === "excel") excelFileLoading.value = true;
  else sdmxFileLoading.value = true;
  http
    .post(
      `${process.env.VUE_APP_BASE_API_URL}api/v2/info/file/upload`,
      formData,
      {
        headers: {
          Authorization: store.state.accessToken,
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then(({ data }) => {
      if (type === "excel") {
        form.excel_file_id = data.hashId;
        excelFileName.value = file.name;
      } else {
        form.sdmx_file_id = data.hashId;
        sdmxFileName.value = file.name;
      }
      formRef.value?.clearValidate(["excel_file_id", "sdmx_file_id"]);
    })
    .catch(() => {
      ElMessage.error("Fayl yuklashda xatolik. Qayta urinib ko'ring!");
    })
    .finally(() => {
      if (type === "excel") excelFileLoading.value = false;
      else sdmxFileLoading.value = false;
      event.target.value = "";
    });
};

const buildPayload = () => {
  const payload = {
    sector_type: form.sector_type,
    period_type: form.period_type,
    year: form.year,
  };
  if (form.excel_file_id) payload.excel_file_id = form.excel_file_id;
  if (form.sdmx_file_id) payload.sdmx_file_id = form.sdmx_file_id;
  if (showQuarter.value) payload.quarter = form.quarter;
  if (showMonth.value) payload.month = form.month;
  return payload;
};

const submitForm = async () => {
  if (!formRef.value) return;
  formRef.value.validate((valid, fields) => {
    if (!valid) {
      Object.values(fields)
        .flat()
        .forEach((e) => ElMessage.error(e.message));
      if (!form.excel_file_id && !form.sdmx_file_id) {
        ElMessage.error("Kamida bitta fayl (Excel yoki SDMX) yuklanishi shart");
      }
      return;
    }
    submitLoading.value = true;
    const url = id ? `api/v1/gfs-data/${id}` : "api/v1/gfs-data";
    const method = id ? "put" : "post";
    http({ url, data: buildPayload(), method })
      .then(() => {
        ElMessage({ type: "success", message: "Muvaffaqqiyatli saqlandi" });
        router.push("/app/gfs-data");
      })
      .catch(({ response }) => {
        ElNotification.error({
          message:
            response?.data?.message || "Xatolik qayta urinib ko'ring",
        });
      })
      .finally(() => {
        submitLoading.value = false;
      });
  });
};

onMounted(() => {
  if (!id) return;
  loading.value = true;
  http
    .get(`api/v1/gfs-data/${id}`)
    .then(({ data }) => {
      form.excel_file_id = data.excel_file_id || "";
      form.sdmx_file_id = data.sdmx_file_id || "";
      form.sector_type = data.sector_type;
      form.period_type = data.period_type;
      form.year = data.year;
      form.quarter = data.quarter;
      form.month = data.month;
      excelFileName.value = data.excel_file_name || null;
      sdmxFileName.value = data.sdmx_file_name || null;
    })
    .catch(() => {
      ElMessage.error("Ma'lumot yuklashda xatolik!");
    })
    .finally(() => {
      loading.value = false;
    });
});
</script>

<style scoped lang="scss">
.form-title {
  margin-bottom: 20px;
  font-weight: 600;
}
.gfs-form {
  max-width: 600px;
}
.file-upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.file-name {
  color: #606266;
  font-size: 14px;
}
.file-hint {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  a {
    text-decoration: none;
  }
}
</style>
